import axios, { AxiosRequestConfig } from "axios";
import { chunk, merge } from "lodash";
import moment from "moment";

import { sortSmartAllocationsHoldings } from "@/lib/utils";

import { axiosInstance } from "../api/axiosConfig";
import { getBaseAsset } from "../constants/baseAssets";
import { setSAHasError, setSmartAllocation } from "../redux/smart-allocation.slice";
import { store } from "../redux/store";

export const getTemplates = async (options: AxiosRequestConfig) => {
  return await axiosInstance.get(`/sa_template/all`, options);
};

export const getLogs = async (options: AxiosRequestConfig) => {
  return await axiosInstance.get(
    `/trade-engine/smart-allocation/logbook`,
    options,
  );
};

export const TNCApproval = async () => {
  return await axiosInstance.put(
    `/trade-engine/smart-allocation/tnc?tnc_accepted=true`,
  );
};

export const getExitStrategy = async (options: AxiosRequestConfig) => {
  return await axiosInstance.get(
    `/trade-engine/smart-allocation/exit`,
    options,
  );
};

export const addExitStrategy = async (
  data: {
    exit_type: string;
    status: string;
    exit_value: number;
    exit_percentage: number;
    exit_based_on: "based_on_portfolio_total" | "based_on_btc_movement";
  },
  options: AxiosRequestConfig,
) => {
  return await axiosInstance.post(
    `/trade-engine/smart-allocation/exit`,
    data,
    options,
  );
};

export const deleteExitStrategy = async (options: AxiosRequestConfig) => {
  return await axiosInstance.delete(
    `/trade-engine/smart-allocation/exit`,
    options,
  );
};

export const getRebalancing = async (options: AxiosRequestConfig) => {
  return await axiosInstance.get(
    `/trade-engine/smart-allocation/frequency`,
    options,
  );
};

export const getRebalancingPortfolio = async (options: AxiosRequestConfig) => {
  return await axiosInstance.get(
    `/trade-engine/smart-allocation/portfolio-asset-rebalancing`,
    options,
  );
};

export const getRebalancingRules = async (options: AxiosRequestConfig) => {
  return await axiosInstance.get(
    `/trade-engine/smart-allocation/coin-movement-rebalancing`,
    options,
  );
};

export const addRebalancing = async (
  data: any,
  options: AxiosRequestConfig,
) => {
  return await axiosInstance.post(
    `/trade-engine/smart-allocation/frequency`,
    data,
    options,
  );
};

export const deleteRebalancing = async (options: AxiosRequestConfig) => {
  return await axiosInstance.delete(
    `/trade-engine/smart-allocation/frequency-rebalancing`,
    options,
  );
};

export const deleteCoinRebalancing = async (options: AxiosRequestConfig) => {
  return await axiosInstance.delete(
    `/trade-engine/smart-allocation/coin-movement-rebalancing`,
    options,
  );
};

export const deletePortfolioRebalancing = async (
  options: AxiosRequestConfig,
) => {
  return await axiosInstance.delete(
    `/trade-engine/smart-allocation/portfolio-asset-rebalancing`,
    options,
  );
};

export const addRebalancingCoin = async (
  data: any,
  options: AxiosRequestConfig,
) => {
  return await axiosInstance.post(
    `/trade-engine/smart-allocation/coin-movement-rebalancing`,
    data,
    options,
  );
};

export const addRebalancingPortfolio = async (
  data: any,
  options: AxiosRequestConfig,
) => {
  return await axiosInstance.put(
    `/trade-engine/smart-allocation/portfolio-asset-rebalancing`,
    data,
    options,
  );
};

export const getSmartAllocation = async (options: AxiosRequestConfig) => {
  store.dispatch(setSAHasError(false));
  return await axiosInstance
    .get("/trade-engine/smart-allocation", options)
    .then(({ data: { data } }) => {
      const tradableHoldings = data.assets?.sort((a: any, b: any) => {
        if (a.executable > b.executable) {
          return -1;
        } else if (b.executable > a.executable) {
          return 1;
        }
        return 0;
      });

      const hasBaseAsset = tradableHoldings.find(
        (holding: any) =>
          holding?.name?.toLowerCase() ===
          data?.selected_base_asset?.toLowerCase(),
      );

      if (!hasBaseAsset) {
        tradableHoldings?.push(
          getBaseAsset(data?.selected_base_asset?.toLowerCase() ?? "usdt"),
        );
      }

      const finalData = tradableHoldings.map((holding: any) => {
        const currentWeight = holding.current_weight;

        let weight = holding.weight ?? 0;
        if (
          holding.stable &&
          !holding.executable &&
          holding.name !== data.selected_base_asset
        ) {
          weight = currentWeight ?? 0;
        } else if (data?.exists === false) {
          weight = currentWeight ?? 0;
        }

        return {
          ...holding,
          weight,
          executable: holding.executable !== false,
        };
      });
      finalData.sort((a: any, b: any) =>
        sortSmartAllocationsHoldings(a, b, data.selected_base_asset),
      );

      store.dispatch(
        setSmartAllocation({
          ...data,
          assets: finalData,
        }),
      );
    }).catch((error) => {
      if (error.code !== "ERR_CANCELED" && error.code != null)
        store.dispatch(setSAHasError(true));
    });
};

export const periodToIntervalsAndOutputSize: {
  [k: string]: { interval: string; outputsize: number };
} = {
  "1w": {
    interval: "30min",
    outputsize: 2 * 24 * 7,
  },
  "1m": {
    interval: "2h",
    outputsize: 12 * 30,
  },
  "1y": {
    interval: "1day",
    outputsize: 365,
  },
  "5y": {
    interval: "1day",
    outputsize: 365 * 5,
  },
};

function getCustomPeriodIntervalsAndOutputSize(
  startDate: number,
  endDate: number,
) {
  const diffTime = Math.abs(startDate - endDate);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays <= 7) {
    return {
      interval: "30min",
      outputsize: 2 * 24 * diffDays,
    };
  } else if (diffDays <= 30) {
    return {
      interval: "2h",
      outputsize: 12 * diffDays,
    };
  } else if (diffDays <= 365) {
    return {
      interval: "1day",
      outputsize: diffDays,
    };
  } else {
    return {
      interval: "1week",
      outputsize: Math.round(diffDays / 7),
    };
  }
}

export async function getAssetsHistoricalData(
  symbols: string[],
  period: string,
  startDate?: Date,
  endDate?: Date,
  signal?: AbortController,
) {
  const requests = chunk(symbols, 120).map((symbolsBatch) => {
    const params: { [k: string]: any } = {
      symbol: symbolsBatch.join(","),
      apikey: process.env.NEXT_PUBLIC_TWELVE_DATA_API_KEY,
      timezone: "UTC",
    };
    if (period === "custom") {
      if (startDate && endDate) {
        const customPeriod = getCustomPeriodIntervalsAndOutputSize(
          startDate.getTime(),
          endDate.getTime(),
        );
        params.interval = customPeriod.interval;
        params.outputsize = customPeriod.outputsize;
        params.start_date = moment(startDate, "DD-MM-YYYY");
        params.end_date = moment(endDate, "DD-MM-YYYY");
      } else {
        params.interval = periodToIntervalsAndOutputSize["1y"].interval;
        params.outputsize = periodToIntervalsAndOutputSize["1y"].outputsize;
      }
    } else {
      params.interval = periodToIntervalsAndOutputSize[period].interval;
      params.outputsize = periodToIntervalsAndOutputSize[period].outputsize;
    }

    return axios.get(process.env.NEXT_PUBLIC_TWELEVE_API_URL ?? "", {
      params,
      signal: signal?.signal,
    });
  });

  return Promise.all(requests).then((responses) => {
    const res: any = {};
    Object.values(responses).forEach((response) => {
      merge(res, response);
    });
    return res;
  });
}

export const getTradableAssets = async (options: AxiosRequestConfig) => {
  return axiosInstance.get("trade-engine/tradable/symbols", options);
};

export const updateSmartAllocation = async (
  data: any,
  options: AxiosRequestConfig,
  exists: boolean,
) => {
  if (exists)
    return await axiosInstance.put(
      `trade-engine/smart-allocation`,
      data,
      options,
    );
  else
    return await axiosInstance.post(
      `trade-engine/smart-allocation`,
      data,
      options,
    );
};

export const startRebalancing = async (options: AxiosRequestConfig) => {
  return await axiosInstance.get(`trade-engine/smart-allocation/balance`, options);
}

export const updateBaseAsset = async (options: AxiosRequestConfig) => {
  return axiosInstance.put(
    "/trade-engine/smart-allocation/base_asset",
    {},
    options,
  );
};
